import React, { createContext, useCallback, useState, useContext, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import api from '../services/api';
import { useToast } from  '../hooks/toast';
import formatName from '../utils/formatName';
import menuRoute from '../utils/menuRoute';
import { AppError } from '../utils/AppError';

type AuthContextProps = {
	children: React.ReactNode;
}
type User = {
  id: string;
  nome: string;
  nome_fantasia: string;
	avatar_name: string;
	user_name: string;
  doc: string;
  phone: string;
  role: string;
  url_avatar: string;
  cep: string;
  uf: string;
  cid: string;
  bai: string;
  enderecos: EnderecoData[];
  num: string;
  comp: string;
	avatar_url: string;
	email: string;
	rg: string;
	telefones: {
		emp_cod: number;
		id: string;
		numero: string;
		obs: string;
	}[];
}
type EnderecoData = {
  id: string;
  cep: string;
  uf: string;
  cid: string;
  cidcod: string;
  bai: string;
  endereco: string;
  num: string;
  comp: string;
  ponto_ref: string;
  formatted_address: string;
  location: {
    lat: number;
    lng: number;
  };
}
type LojaData = {
  id: string; // number;
  nome: string;
  url_avatar: string;
  url_page: string;
  fantazia: string;
  fone: string;
  endereco: {
    cep: string;
    uf: string;
    cid: string;
    cidcod: string;
    bai: string;
    endereco: string;
    num: string;
    comp: string;
    formatted_address: string;
    location: {
      lat: number;
      lng: number;
    };
  };
}
type MenuData = {
	name: string;
	route: string;
	icon: string;
	sub_menu: SubMenuData[];
	collapse: boolean;
	permissions: PermissionsData;
}
type SubMenuData = {
	name: string;
	route: string;
	icon: string;
	permissions: PermissionsData;
}
type PermissionsData = {
	alterar: boolean;
	baixa: boolean;
	confirmar: boolean;
	excluir: boolean;
	incluir: boolean;
}
type SignInCredentials = {
  email: string;
  password: string;
}
type AuthState = {
	token: string;
	user: User;
	userPintor10: User;
	lojas: LojaData[];
	company: LojaData;
	menu: MenuData[];
	permissions: PermissionsData;
}
interface AuthContexData {
  user: User;
	userPintor10: User;
  lojas: LojaData[];
  company: LojaData;
	token: string;
	menu: MenuData[];
	permissions: PermissionsData;
  signIn(credentials: SignInCredentials): Promise<void>; // Promise<User>;
  signOut(): void;
	signInPintor10(credentials: SignInCredentials): Promise<User>;
	signOutPintor10(): void;
  updateUser(user: User): void;
  setCompany(loja: LojaData): void;
	refreshMenu(): void;
	setMenuCollapse(name: string): void;
  isLoadingUserStorageData: boolean;
}

const AuthContext = createContext<AuthContexData>({} as AuthContexData);

const AuthProvider: React.FC<AuthContextProps> = ({ children }) => {
	const history = useHistory();
	const location = useLocation();
	const { addToast } = useToast();

  const [isLoadingUserStorageData, setIsLoadingUserStorageData] = useState(true);
  const [data, setData] = useState<AuthState>({} as AuthState);

	async function signIn({ email, password }: SignInCredentials) {
		try {
			const { data } = await api.post('portal/sessions', { email, password });

			const { output, user, token, lojas, menu } = data;

			if (output.che === 'N')
				throw new AppError(output.men);

			const company = lojas[0];

			user.user_name = user.nome.split(' ').slice(0, 2).join(' ');
			user.avatar_name = formatName(user.nome);

			localStorage.setItem('@PortalRazio:token', token);
			localStorage.setItem('@PortalRazio:user', JSON.stringify(user));
			localStorage.setItem('@PortalRazio:lojas', JSON.stringify(lojas));
			localStorage.setItem('@PortalRazio:company', JSON.stringify(lojas[0]));
			
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['company'] = lojas[0].id;

			const permissions = await menuRoute('/dashboard', menu);

			console.log('permissions ==> ', permissions);

			setData({ ...data, token, user, userPintor10: {} as User, lojas, company, menu, permissions });

		} catch (error) {
			console.log('error => ', error);
			throw error;
		}
  };

  const signOut = useCallback(() => {
    localStorage.removeItem('@PortalRazio:token');
    localStorage.removeItem('@PortalRazio:user');
		localStorage.removeItem('@PortalRazio:userPinto10');
    localStorage.removeItem('@PortalRazio:lojas');
    localStorage.removeItem('@PortalRazio:company');

    setData({} as AuthState);

    history.push('/login');
  }, [history]);

	const signInPintor10 = useCallback(async ({ email, password }: SignInCredentials) => {
		try {
			const { data } = await api.post('/pintor10/sessions', { email, password });

			const { user, token } = data;

			user.user_name = user.name.split(' ').slice(0, 2).join(' ');
			user.avatar_name = formatName(user.nome);

			localStorage.setItem('@PortalRazio:token', token);
			localStorage.setItem('@PortalRazio:userPintor10', JSON.stringify(user));

			api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			api.defaults.headers.common['company'] = user.emp_cod;

			setData({
				token,
				user: {} as User,
				userPintor10: user,
				lojas: [],
				company: {} as LojaData,
				menu: [] as MenuData[],
				permissions: {} as PermissionsData
			});

			history.push('/accountdelete');

			return user;
		}
		catch (error) {
			throw error;
		}
	}, []);

	const signOutPintor10 = useCallback(() => {
		localStorage.removeItem('@PortalRazio:token');
		localStorage.removeItem('@PortalRazio:user');
		localStorage.removeItem('@PortalRazio:userPinto10');
		localStorage.removeItem('@PortalRazio:lojas');
		localStorage.removeItem('@PortalRazio:company');

		setData({} as AuthState);

		history.push('/');
	}, [history]);

  const updateUser = useCallback((user: User) => {
		localStorage.setItem('@PortalRazio:user', JSON.stringify(user));

		setData({ ...data, user });
	}, [setData, data]);

  const setCompany = useCallback((loja: LojaData) => {
    api.defaults.headers.common['company'] = loja.id;
    localStorage.setItem('@PortalRazio:company', JSON.stringify(loja));

    setData(item => {
      return { ...item, company: loja };
    });
  }, []);

	async function refreshMenu() {
		try {
			let permissions = {} as PermissionsData;

			const response = await api.get('/portal/menu');

			const auxMenu = await response.data.map((item: MenuData) => {
				if (item.sub_menu) {
					const auxSub = item.sub_menu.find(sub => sub.route === location.pathname);

					if (auxSub) {
						item.collapse = true;
						permissions = auxSub.permissions;
					} else {
						item.collapse = false;
					}

					return item;
				}

				permissions = item.permissions;

				return item;
			});

			// const permissions = await menuRoute(history.location.pathname, auxMenu);

			setData({ ...data, menu: auxMenu, permissions });

		} catch (error) {
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Não foi possível buscar o menu do usuário.';

			addToast({
				type: 'error',
				title: 'Buscar Menu!',
				description: msg,
			});
		}
	};

	async function setMenuCollapse(name: string) { //const setMenuCollapse = useCallback((name: string) => {
		const indexMenu = data.menu.findIndex(item => item.name === name);

		const auxMenu = data.menu;
		auxMenu[indexMenu] = { ...auxMenu[indexMenu], collapse: !auxMenu[indexMenu].collapse };

		setData({ ...data, menu: auxMenu });
	}; //,[setData, data]);

  async function loadUserData() {
    try {
      setIsLoadingUserStorageData(true);

      const token = localStorage.getItem('@PortalRazio:token');
      const user = localStorage.getItem('@PortalRazio:user');
      const userPintor10 = localStorage.getItem('@PortalRazio:userPintor10');
      const lojas = localStorage.getItem('@PortalRazio:lojas');
      const company = localStorage.getItem('@PortalRazio:company');

      const auxData = {} as AuthState;

      if (user && token) {
        auxData.user = JSON.parse(user);
        auxData.token = token;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        auxData.menu = [] as MenuData[];
        auxData.permissions = {} as PermissionsData;
      }

      if (userPintor10 && token) {
        auxData.userPintor10 = JSON.parse(userPintor10);
        auxData.token = token;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        api.defaults.headers.common['company'] = JSON.parse(userPintor10).emp_cod;
      }

      if (lojas)
        auxData.lojas = JSON.parse(lojas);

      if (company) {
        auxData.company = JSON.parse(company);
        api.defaults.headers.common['company'] = parseInt(JSON.parse(company).id);
      }

      setData(auxData);
    }
    catch (error) {
      console.log('ERROR LoadUSerData ==> ', error);
      throw error;
    }
    finally {
      setIsLoadingUserStorageData(false);
    }
  }

  useEffect(() => {
		console.log('LOADING USER');
    const loadUser: any = async () =>  await loadUserData();
		loadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
				userPintor10: data.userPintor10,
        lojas: data.lojas,
        company: data.company,
				token: data.token,
				menu: data.menu,
				permissions: data.permissions,
        signIn,
        signOut,
				signInPintor10,
				signOutPintor10,
        updateUser,
        setCompany,
				refreshMenu,
				setMenuCollapse,
        isLoadingUserStorageData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContexData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
